import { ICE_BOT_LEVELS, type IceBotLevel } from '@halvard/ai';
import { SIDE_NAMES } from '@halvard/board3d';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useIceBotDriver } from '../bot/useIceBotDriver';
import { IceLocalGame } from '../game/ice/IceLocalGame';
import { createIceGameStore, type IcePlayer, type IcePlayers } from '../game/ice/store';
import { parseSeed, randomSeed } from '../game/mountain';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './PlayBot.css';

function parsePlayer(text: string | null): IcePlayer | null {
  if (text === 'human') return 'human';
  return ICE_BOT_LEVELS.find((l) => l.id === text)?.id ?? null;
}

/** Players from `?brand=human&rime=hard`; at least one side must be a bot. */
export function icePlayersFromParams(params: URLSearchParams): IcePlayers | null {
  const brand = parsePlayer(params.get('brand'));
  const rime = parsePlayer(params.get('rime'));
  if (!brand || !rime || (brand === 'human' && rime === 'human')) return null;
  return { brand, rime };
}

/**
 * `/play/bot?brand=human&rime=hard&lake=482193`: play the ice game against a bot, or watch two
 * bots. Without players, it shows the setup.
 */
export function PlayIceBot() {
  const [params, setParams] = useSearchParams();
  const players = icePlayersFromParams(params);
  const seed = parseSeed(params.get('lake'));
  const watching = players?.brand !== 'human' && players?.rime !== 'human';
  useDocumentTitle(players && watching ? 'Bot vs bot' : 'Play the bot');

  if (!players) {
    return (
      <IceBotSetup
        onStart={(p) =>
          setParams({ brand: p.brand, rime: p.rime, lake: String(seed ?? randomSeed()) })
        }
      />
    );
  }
  const lake = seed ?? randomSeed();
  return (
    <IceBotGame
      key={`${lake}|${players.brand}|${players.rime}`}
      seed={lake}
      players={players}
      onNewLake={() =>
        setParams({ brand: players.brand, rime: players.rime, lake: String(randomSeed()) })
      }
    />
  );
}

function IceBotGame(props: { seed: number; players: IcePlayers; onNewLake: () => void }) {
  const [store] = useState(() => createIceGameStore(props.seed, randomSeed(), props.players));
  const [botSeed] = useState(randomSeed);
  useIceBotDriver(store, botSeed);
  return (
    <IceLocalGame
      store={store}
      onNewLake={props.onNewLake}
      onReplay={() => store.getState().start(props.seed, randomSeed())}
      panelExtra={
        <div className="game-section">
          <Link to="/play/bot">Change bots or sides</Link>
        </div>
      }
    />
  );
}

type Mode = 'brand' | 'rime' | 'watch';

const MODES: { id: Mode; title: string; detail: string }[] = [
  { id: 'brand', title: 'Play Brand', detail: 'You attack: take the runestones before the thaw.' },
  { id: 'rime', title: 'Play Rime', detail: 'You defend: hold the lake until round 16.' },
  { id: 'watch', title: 'Watch two bots', detail: 'Both sides are played by bots.' },
];

function IceBotSetup({ onStart }: { onStart: (players: IcePlayers) => void }) {
  const [mode, setMode] = useState<Mode>('brand');
  const [brandLevel, setBrandLevel] = useState<IceBotLevel>('normal');
  const [rimeLevel, setRimeLevel] = useState<IceBotLevel>('normal');
  const players: IcePlayers =
    mode === 'brand'
      ? { brand: 'human', rime: rimeLevel }
      : mode === 'rime'
        ? { brand: brandLevel, rime: 'human' }
        : { brand: brandLevel, rime: rimeLevel };

  return (
    <article className="page-page bot-setup">
      <h1>Play the bot</h1>
      <fieldset className="bot-picker">
        <legend>You</legend>
        <div className="bot-strategies">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className="bot-strategy"
              aria-pressed={mode === m.id}
              onClick={() => setMode(m.id)}
            >
              <strong>{m.title}</strong>
              <span>{m.detail}</span>
            </button>
          ))}
        </div>
      </fieldset>
      {mode !== 'brand' && <LevelPicker side="brand" value={brandLevel} onChange={setBrandLevel} />}
      {mode !== 'rime' && <LevelPicker side="rime" value={rimeLevel} onChange={setRimeLevel} />}
      <p>
        <button type="button" className="btn btn-primary" onClick={() => onStart(players)}>
          Start
        </button>
      </p>
    </article>
  );
}

function LevelPicker(props: {
  side: 'brand' | 'rime';
  value: IceBotLevel;
  onChange: (level: IceBotLevel) => void;
}) {
  return (
    <fieldset className="bot-picker">
      <legend>{SIDE_NAMES[props.side]} bot</legend>
      <div className="bot-strategies">
        {ICE_BOT_LEVELS.map((level) => (
          <button
            key={level.id}
            type="button"
            className="bot-strategy"
            aria-pressed={props.value === level.id}
            onClick={() => props.onChange(level.id)}
          >
            <strong>{level.name}</strong>
            <span>{level.detail}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}
